import { Fragment, useCallback, useEffect, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import BSForm from "react-bootstrap/Form";
import { IoIosArrowDown } from "react-icons/io";
import { BsPlusLg } from "react-icons/bs";
import styled from "styled-components";
import AnimateHeight from "react-animate-height";
import {
  CustomButton,
  CustomSelect,
  CustomControl,
  CustomNumberInput,
  FormikFieldError,
  CustomInput,
} from "components/CustomForm";
import useModal from "hooks/useModal";
import PersianPickerTile from "components/PersianPickerTile";
import { createDistribute, updateDistribute } from "network/apis/DistributeApi";
import {
  getUnits,
  getIngredients,
  IN_STOCK,
  OUT_OF_STOCK,
} from "network/apis/CommonApi";
import { getDistsReport } from 'network/apis/ReportingApi';
import { openLink } from 'helpers/general';
import { getUnions } from 'network/apis/InformationApi';
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { ErrorMessage, Field, Formik, Form } from "formik";
import { isBlank, getErrorMessage } from "helpers/strings";
import { getFormattedDate } from "helpers/dates";
import { useCompanySelector, useCompanyDispatch } from 'company-app/redux/company.store';
import {
  handleDateRadioChange,
  pickFromDate,
  pickToDate,
  handleInputChange,
  goToPage,
  buildFiltersQuery,
  DateRadio,
  DefaultConcentrateFilter,
} from "company-app/redux/filter.slice";
import Skeleton from "react-loading-skeleton";
import { getDistributeStatistics } from "network/apis/DistributeApi";
import { getCorporations } from 'network/apis/CorporationApi';
import PersianPickerInput from "components/PersianPickerInput";
import useScreen from 'hooks/useScreen';
import DistPrompt from "components/DistPrompt";
import CommonTable from "components/CommonTable";

const Check = styled(BSForm.Check)`
  display: inline;
`;

const ResultBox = styled.div`
  border: 1px solid black;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem;
`;

const ToggleCollapse = styled.span`
  background-color: #784af6;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 1rem;
  cursor: pointer;
  position: absolute;
  bottom: -1rem;
  transform: rotate(${(props) => (props.open ? "180deg" : "0")});

  transition: all 300ms ease-in-out;
`;

const FormLabel = styled.label`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const ProductsSelect = styled(CustomSelect)`
    width: 50%;
`;

const Holder = styled.div`
  display: flex;
  column-gap: 12px;
  justify-content: space-between;
  padding: 1.7rem 0 1.5rem 1.1rem;
  direction: rtl;

  & > *:not(button) {
    flex: 1 1 0;
    width: 0;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    row-gap: 1rem;
  
    & > *:not(button) {
      flex: 1;
      width: inherit;
    }
  }
`;

const HDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #c6c6c6;
  margin-top: 2rem;
`;

const DistributeType = {
  corporation: { label: 'شرکت', value: 0 },
  union: { label: 'اتحادیه', value: 1 },
}

const AdminDistributeTabMenu = ({ tabData, filterQuery, onCollapse, onCreateSuccess }) => {
  const [currentData, setCurrentData] = useState();
  const isEditMode = useCallback(
    () => currentData && currentData != null,
    [currentData]
  );

  const [loading, setLoading] = useState(false);
  const [distributeTo, setDistributeTo] = useState(DistributeType.union);
  const [selectedCorporation, setSelectedCorporation] = useState();
  const [selectedUnion, setSelectedUnion] = useState();
  const [selectedIngredient, setSelectedIngredient] = useState();
  const [selectedUnit, setSelectedUnit] = useState();
  const [selectedDate, setSelectedDate] = useState(getFormattedDate());
  const [collapseHeight, setCollapseHeight] = useState(0);
  const [formikKey, setFormikKey] = useState(1);

  const filterState = useCompanySelector((store) => store.filter);
  const dispatch = useCompanyDispatch();

  const { size, lessThan1200, lessThan900, lessThan768 } = useScreen();

  const {
    isOpen: isPromptOpen,
    handleOpenModal: handleOpenPrompt,
    handleCloseModal: handleClosePrompt,
    data: promptData,
  } = useModal();

  useEffect(() => {
    setCurrentData(tabData);
  }, [tabData]);

  useEffect(() => {
    const isEditMode = currentData && currentData != null;

    setCollapseHeight(isEditMode ? "auto" : 0);

    setSelectedIngredient(
      isEditMode
        ? { label: currentData.input.name, value: currentData.input.input_id }
        : null
    );
    setSelectedUnit(
      isEditMode
        ? { label: currentData.unit.name, value: currentData.unit.unit_id }
        : null
    );
    if (isEditMode && currentData?.date) {
      setSelectedDate(currentData?.date);
    }

    setFormikKey((prev) => prev + 1);
  }, [currentData, isEditMode]);

  const { data: statisticsData, isLoading: statisticsDataIsLoading } = useQuery(
    ["get-dist-statistics", filterQuery],
    () => getDistributeStatistics(filterQuery),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: inputsData } = useQuery(
    ["get-ingredients"],
    () => getIngredients(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: unitsData } = useQuery(["get-units"], () => getUnits(), {
    refetchOnWindowFocus: false,
  });

  const addMutation = useMutation(
    isEditMode() ? updateDistribute : createDistribute,
    {
      onSuccess: (res) => {
        if (res && res.data) {
          toast(`با موفقیت ${isEditMode() ? "ویرایش" : "ایجاد"} گردید`, {
            type: "success",
            position: "bottom-right",
          });
          onCreateSuccess?.();
          onToggle();
        }
      },
      onError: (err) => {
        toast(getErrorMessage(err), {
          type: "error",
          position: "bottom-right",
        });
      },
      onSettled: () => {
        setLoading(false);
      },
    }
  );

  const resetInputs = () => {
    setCurrentData(null);
    setSelectedIngredient(null);
    setSelectedDate(getFormattedDate());
    setFormikKey((prev) => prev + 1);
  };

  const getInputOtions = () => {
    if (inputsData?.data) {
      const inputOptions = inputsData.data.map((input, index) => {
        return { value: input?.item_id, label: input?.name };
      });

      return [DefaultConcentrateFilter, ...inputOptions];
    }

    return [];
  };

  const getUnitOptions = () =>
    unitsData?.data
      ? unitsData.data.map((unit, index) => {
        return { value: unit?.unit_id, label: unit?.name };
      })
      : [];

  const onToggle = () => {
    if (collapseHeight === "auto") resetInputs();
    onCollapse?.(collapseHeight === 0);
    setCollapseHeight(collapseHeight === 0 ? "auto" : 0);
  };

  const onInputChange = (newVal) => {
    setSelectedIngredient(newVal);

    const targetInput = inputsData?.data?.find((i) => i.item_id);
    if (targetInput) {
      const defaultUnit = {
        value: targetInput.default_unit?.unit_id,
        label: targetInput.default_unit?.name
      };
      setSelectedUnit(defaultUnit);
    }
  };

  const { data: corporationsData } = useQuery(["get-corporations"], () => getCorporations(), {
    refetchOnWindowFocus: false,
  });
  const getCorporationOptions = () =>
    corporationsData?.data
      ? corporationsData.data.map((corporation, index) => {
        return { value: corporation?.coop_id, label: corporation?.name };
      })
      : [];

  const { data: unionsData } = useQuery(["get-unions"], () => getUnions(), {
    refetchOnWindowFocus: false,
  });
  const getUnionsOptions = () =>
    unionsData?.data
      ? unionsData.data.map((union, index) => {
        return { value: union?.union_id, label: union?.name };
      })
      : [];

  const isDistForUnions = () => distributeTo?.value === DistributeType.union?.value;
  const isDistForCorporations = () => distributeTo?.value === DistributeType.corporation?.value;

  const { refetch: fetchTableExcel } = useQuery(
    ["get-admin-dist-table-excel", filterQuery],
    () => getDistsReport(filterQuery + 'excel=true'),
    {
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (data) => {
        const url = data?.list?.data?.url;
        if (url) openLink(url);
      },
    }
  );

  const downloadTableExcel = () => {
    fetchTableExcel();
  };

  const dispatchFilters = () => {
		dispatch(goToPage(1));
		dispatch(buildFiltersQuery());
	};

  return (
    <Fragment>
      <Col
        md={lessThan768 ? 12 : 4}
        className={`d-flex align-items-center justify-content-${lessThan768 ? 'center mb-4' : 'end'}`}
      >
        <div className='mx-5 d-flex flex-column align-items-center gap-2'>
          <CustomButton
            fontSize={10}
            onClick={downloadTableExcel}
            color='#0278EE'
          >
            اکسل جدول
          </CustomButton>
        </div>
        <ResultBox className="w-100">
          {statisticsDataIsLoading ? (
            <Skeleton count={1} width={34} height={20} />
          ) : (
            <span>{statisticsData?.data?.unit?.name}</span>
          )}
          {statisticsDataIsLoading ? (
            <Skeleton count={1} width={60} height={20} />
          ) : (
            <span>{`${statisticsData?.data?.sum ?? 0}`.persianCurrency()}</span>
          )}
          <span>:مجموع</span>
        </ResultBox>
      </Col>
      <Col md={lessThan768 ? 12 : 8}>
        <div
          className={`${lessThan900 ? 'ps-4' : 'ms-5 ps-5'} d-flex justify-content-between align-items-center`}
        >
          <div className="d-inline-flex flex-wrap align-items-center">
            <PersianPickerTile
              currentDate={filterState.pickedDateTo}
              topOffset={lessThan1200 ? '36%' : '18%'}
              leftOffset={lessThan1200 ? '3%' : '37%'}
              onPick={(pickedDate) => dispatch(pickToDate(pickedDate))}
            />
            <span className="mx-2">تا</span>
            <PersianPickerTile
              currentDate={filterState.pickedDateFrom}
              topOffset={lessThan1200 ? '36%' : '18%'}
              leftOffset={lessThan1200 ? '11%' : '49%'}
              onPick={(pickedDate) => dispatch(pickFromDate(pickedDate))}
            />
          </div>
          <div className="d-inline-flex flex-wrap justify-content-end">
            <FormLabel htmlFor={DateRadio.CustomDate}>بازه زمانی دلخواه</FormLabel>
            <Check
              className={`${lessThan1200 ? '' : 'me-3'} ms-2`}
              type="radio"
              id={DateRadio.CustomDate}
              checked={filterState.selectedRadio === DateRadio.CustomDate}
              onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
            />
            <FormLabel
              htmlFor={DateRadio.LastMonth}
              className={`${size.width < 560 ? 'mt-3' : ''}`}
            >
              توزیع های یک ماه گذشته
            </FormLabel>
            <Check
              className={`ms-1 ${size.width < 560 ? 'mt-3' : ''}`}
              type="radio"
              id={DateRadio.LastMonth}
              checked={filterState.selectedRadio === DateRadio.LastMonth}
              onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
            />
          </div>
        </div>
        <div
          className={`${lessThan900 ? '' : 'ms-5 ps-5'} mt-3 d-flex justify-content-between align-items-center flex-wrap-reverse`}
        >
          <CustomButton
            className={`${lessThan900 ? 'w-100 mt-3 ms-4' : ''}`}
            onClick={dispatchFilters}
          >
            نمایش
          </CustomButton>
          <div className='d-inline-flex align-items-center justify-content-end flex-grow-2'>
            <ProductsSelect
              className="w-100 mx-4"
              radius="18"
              options={getInputOtions()}
              onChange={(newInput) => dispatch(handleInputChange(newInput))}
              value={filterState.selectedInput}
              menuPosition='fixed'
            />
            <FormLabel htmlFor={DateRadio.LastThreeMonths} className='text-end flex-shrink-0'>
              توزیع های سه ماه گذشته
            </FormLabel>
            <Check
              className="ms-2"
              type="radio"
              id={DateRadio.LastThreeMonths}
              checked={filterState.selectedRadio === DateRadio.LastThreeMonths}
              onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
            />
          </div>
        </div>
      </Col>
      <ToggleCollapse
        onClick={() => onToggle()}
        open={collapseHeight === "auto"}
      >
        <div>
          <IoIosArrowDown size={22} />
        </div>
      </ToggleCollapse>
      <AnimateHeight height={collapseHeight} duration={500}>
        <HDivider />
        <Formik
          key={formikKey}
          initialValues={{
            amount: `${currentData?.amount ?? ""}`,
            description: currentData?.description,
            cost: currentData?.cost,
          }}
          validate={(values) => {
            const errors = {};

            if (isDistForCorporations() && !selectedCorporation) {
              errors.corp_id = "ضروری";
            } else if (isDistForUnions() && !selectedUnion) {
              errors.union_id = "ضروری";
            }

            if (!selectedIngredient) {
              errors.input_id = "ضروری";
            } else if (isBlank(values.amount)) {
              errors.amount = "ضروری";
            } else if (isBlank(values.cost)) {
              errors.cost = "ضروری";
            } else if (!selectedUnit) {
              errors.unit_id = "ضروری";
            } else if (isBlank(values.description)) {
              errors.description = "ضروری";
            }
            return errors;
          }}
          onSubmit={(values) => {
            setLoading(true);

            addMutation.mutate({
              id: currentData?.dist_id,
              conc_id: null,
              input_id: selectedIngredient?.value,
              date: selectedDate,
              unit_id: selectedUnit?.value,
              transaction: {
                destination_id: isDistForUnions() ? selectedUnion?.value : selectedCorporation?.value,
                destination_type: isDistForUnions() ? DistributeType.union.value : DistributeType.corporation.value
              },
              ...values,
            });
          }}
        >
          {({ submitForm, values }) => (
            <Form>
              <Holder>
                <div>
                  <Field type="text" name="dist_type">
                    {({ field, meta: { touched, error } }) => (
                      <CustomSelect
                        className={touched && error ? "invalid" : ""}
                        error={!isBlank(error)}
                        {...field}
                        radius="14"
                        options={Object.values(DistributeType)}
                        onChange={setDistributeTo}
                        value={distributeTo}
                        menuPosition='fixed'
                        isDisabled={isEditMode()}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="dist_type"
                    component={FormikFieldError}
                  />
                </div>
                {isDistForCorporations() && (
                  <div>
                    <Field type="text" name="corp_id">
                      {({ field, meta: { touched, error } }) => (
                        <CustomSelect
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="انتخاب شرکت تعاونی"
                          radius="14"
                          options={getCorporationOptions()}
                          onChange={setSelectedCorporation}
                          value={selectedCorporation}
                          menuPosition='fixed'
                          isDisabled={isEditMode()}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="corp_id"
                      component={FormikFieldError}
                    />
                  </div>
                )}
                {isDistForUnions() && (
                  <div>
                    <Field type="text" name="union_id">
                      {({ field, meta: { touched, error } }) => (
                        <CustomSelect
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="انتخاب اتحادیه"
                          radius="14"
                          options={getUnionsOptions()}
                          onChange={setSelectedUnion}
                          value={selectedUnion}
                          menuPosition='fixed'
                          isDisabled={isEditMode()}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="union_id"
                      component={FormikFieldError}
                    />
                  </div>
                )}
                <div>
                  <Field type="text" name="input_id">
                    {({ field, meta: { touched, error } }) => (
                      <CustomSelect
                        className={touched && error ? "invalid" : ""}
                        error={!isBlank(error)}
                        {...field}
                        placeholder="نوع نهاده"
                        radius="14"
                        options={getInputOtions()}
                        onChange={onInputChange}
                        value={selectedIngredient}
                        isDisabled={isEditMode()}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="input_id" component={FormikFieldError} />
                </div>
                <div>
                  <Field type="text" name="amount">
                    {({ field, meta: { touched, error } }) => (
                      <CustomNumberInput
                        className={touched && error ? "invalid" : ""}
                        error={!isBlank(error)}
                        {...field}
                        placeholder="مقدار"
                        radius="14"
                      />
                    )}
                  </Field>
                  <ErrorMessage name="amount" component={FormikFieldError} />
                </div>
                <div>
                  <Field type="number" name="cost">
                    {({ field, meta: { touched, error } }) => (
                      <CustomNumberInput
                        className={`order-2 ${touched && error ? "invalid" : ""}`}
                        error={!isBlank(error)}
                        {...field}
                        placeholder="مبلغ (ریال)"
                        radius="14"
                      />
                    )}
                  </Field>
                  <ErrorMessage name="cost" component={FormikFieldError} />
                </div>
                <div>
                  <Field type="text" name="unit_id">
                    {({ field, meta: { touched, error } }) => (
                      <CustomSelect
                        className={touched && error ? "invalid" : ""}
                        error={!isBlank(error)}
                        {...field}
                        placeholder="واحد"
                        radius="14"
                        options={getUnitOptions()}
                        onChange={setSelectedUnit}
                        value={selectedUnit}
                        isDisabled={isEditMode()}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="unit_id" component={FormikFieldError} />
                </div>
                <div className="grow-2">
                  <Field name="date">
                    {({ field, meta: { touched, error } }) => (
                      <PersianPickerInput
                        currentDate={selectedDate}
                        topOffset={90}
                        leftOffset={25.5}
                        key={selectedDate ?? 'e'}
                        onPick={setSelectedDate}
                        label='تاریخ توزیع'
                        field={field}
                        touched={touched}
                        error={error}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="date" component={FormikFieldError} />
                </div>
                <div>
                  <Field type="text" name="description">
                    {({ field, meta: { touched, error } }) => (
                      <CustomInput
                        className={touched && error ? "invalid" : ""}
                        error={!isBlank(error)}
                        {...field}
                        placeholder="توضیحات"
                        radius="14"
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="description"
                    component={FormikFieldError}
                  />
                </div>
                <CustomButton
                  onClick={() => {
                    handleOpenPrompt({
                      ingredient: selectedIngredient?.label,
                      date: selectedDate,
                      unit: selectedUnit?.label,
                      destination_type: isDistForUnions() ? DistributeType.union.label : DistributeType.corporation.label,
                      ...values,
                    });
                  }}
                  disabled={loading}
                  className="d-flex align-items-center justify-content-center"
                >
                  {loading ? (
                    <Spinner animation="border" role="status" size="sm" />
                  ) : (
                    <span>{isEditMode() ? "ویرایش" : "ثبت"}</span>
                  )}
                </CustomButton>
              </Holder>
              {isPromptOpen && (
                <DistPrompt
                  isOpen={isPromptOpen}
                  onClose={handleClosePrompt}
                  data={promptData}
                  onActionTap={() => {
                    handleClosePrompt();
                    submitForm();
                  }}
                >
                  <CommonTable
                    cols={['نوع توزیع', 'نوع نهاده', 'مقدار', 'مبلغ (ریال)', 'واحد', 'تاریخ توزیع', 'توضیحات'].reverse()}
                    tablewidth='100%'
                  >
                    <tr>
                      <td>{promptData?.description ?? '-'}</td>
                      <td>{promptData?.date ?? '-'}</td>
                      <td>{promptData?.unit ?? '-'}</td>
                      <td>{promptData?.cost ?? '-'}</td>
                      <td>{promptData?.amount ?? '-'}</td>
                      <td>{promptData?.ingredient ?? '-'}</td>
                      <td>{promptData?.destination_type ?? '-'}</td>
                    </tr>
                  </CommonTable>
                </DistPrompt>
              )}
            </Form>
          )}
        </Formik>
      </AnimateHeight>
    </Fragment>
  );
};

export default AdminDistributeTabMenu;
